import { memo, useMemo } from 'react';
import type { SortScoreVersion } from '@/atoms/typesenseDebug';
import { FreeFirstTrackSections, type TrackSection } from '@/components/FreeFirstTrackSections';
import { createStaticPageQueueAtom } from '@/features/AudioPlayer/atoms/queue/derivedQueueAtoms';
import { type FreeFirstSfxTierResult, useFreeFirstSfxSearch } from '@/hooks/search/useFreeFirstSfxSearch';
import { useSfxResultsWithContributorSlugs } from '@/hooks/useSfxResultsWithContributorSlugs';
import { ASSET_TYPE } from '@/types/asset';
import type { SearchResponseHit, SfxDocument, SortOption } from '@music-vine/setlist';
import { normalizeSfxDocumentForSearch } from '@/utils/track-documents';

// Stamp each raw hit as an SFX asset: playback keys off `asset_type === 'sfx'`
// + a numeric `sfx_variant_id`, neither of which the raw document guarantees.
const asSfxHits = (hits: SearchResponseHit<SfxDocument>[]) => hits.map(hit => ({
  ...hit,
  document: {
    ...hit.document,
    asset_type: 'sfx' as const,
    sfx_variant_id: Number(hit.document.sfx_variant_id)
  }
}));

// sfx.v3 rows don't ship `contributor_slug`, so results run through the shared
// slug-enrichment hook before rendering.
const useTierSection = ({
  tier,
  queueSource,
  queueName,
  queueId,
  isLoading,
  isError
}: {
  tier: FreeFirstSfxTierResult;
  queueSource: string;
  queueName: string;
  queueId: string;
  isLoading: boolean;
  isError: boolean;
}): TrackSection => {
  const sfxHits = useMemo(() => asSfxHits(tier.hits), [tier.hits]);
  const baseResults = useMemo(() => sfxHits.map(hit => normalizeSfxDocumentForSearch(hit.document)), [sfxHits]);
  const results = useSfxResultsWithContributorSlugs(baseResults);
  const pageQueueAtom = useMemo(() => createStaticPageQueueAtom(sfxHits, {
    source: queueSource,
    sourceType: 'search',
    sourceName: queueName,
    sourceId: queueId
  }), [sfxHits, queueSource, queueName, queueId]);
  return {
    results,
    pageQueueAtom,
    hasMore: tier.hasMore,
    isFetchingMore: tier.isFetchingMore,
    onShowMore: tier.onShowMore,
    pageIndex: tier.pageCount,
    isLoading,
    isError
  };
};
interface FreeFirstSfxSearchResultsProps {
  contextPath: string;
  // `false` on global /sfx/all; the page's public path on collection/category
  // routes, so split sections hit the same engagement-sorted collection.
  contextSlug: string | false;
  filters: string;
  premiumTitle?: string;
  query: string;
  sort: SortOption;
  sortScoreVersion: SortScoreVersion;
}
export const FreeFirstSfxSearchResults = memo(({
  query,
  filters,
  sort,
  sortScoreVersion,
  contextPath,
  contextSlug,
  premiumTitle
}: FreeFirstSfxSearchResultsProps) => {
  const {
    free,
    premium,
    isLoading,
    isError
  } = useFreeFirstSfxSearch({
    query,
    filters,
    sort,
    sortScoreVersion,
    contextPath,
    contextSlug
  });
  const freeSection = useTierSection({
    tier: free,
    queueSource: `${contextPath}:free-first-free`,
    queueName: 'Free sound effects',
    queueId: `${query}:free`,
    isLoading,
    isError
  });
  const premiumSection = useTierSection({
    tier: premium,
    queueSource: `${contextPath}:free-first-premium`,
    queueName: 'Premium sound effects',
    queueId: `${query}:premium`,
    isLoading,
    isError
  });
  return <FreeFirstTrackSections assetType={ASSET_TYPE.SFX} free={freeSection} premium={premiumSection} premiumTitle={premiumTitle} />;
});
FreeFirstSfxSearchResults.displayName = 'FreeFirstSfxSearchResults';